import { useCreation, useMemoizedFn, useUpdateEffect } from 'ahooks';
import { clone, equals, isNil, remove, update } from 'ramda';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { isArray, isNone, isNumber, isString } from '../_util/defineType';
import { generateUuid } from '../_util/utils';
import Item from './item';
import { last } from './utils';
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
const formatSearchValue = (data, configuration) => {
  return Object.keys(data).reduce((acc, key) => {
    const value = data[key];
    const cur = configuration.find(item => item.key === key);
    if (!cur || isNone(value)) {
      return acc;
    }
    if (isArray(value) && cur.mode === 'multiple-select') {
      const valArr = value.flatMap(it => !isNone(it) && (isString(it) || isNumber(it)) ? [it] : []);
      if (valArr.length > 0) {
        acc.push({
          ...cur,
          value: valArr
        });
      }
    } else if (isString(value) || isNumber(value)) {
      acc.push({
        ...cur,
        value
      });
    }
    return acc;
  }, []);
};
const GroupSearch = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    defaultValue = {},
    value,
    configuration = [],
    placeholder,
    onSearch,
    baseClassName,
    isFocusWithin,
    toggleClearIcon
  } = props;
  const [focusItemKey, setFocusItemKey] = useState({});
  const [settledItems, setSettledItems] = useState([...formatSearchValue(value !== null && value !== void 0 ? value : defaultValue, configuration), {
    key: generateUuid(),
    label: '',
    value: '',
    mode: 'text'
  }]);
  const settledItemkeys = useCreation(() => {
    return settledItems.map(_ref => {
      let {
        key
      } = _ref;
      return key;
    });
  }, [settledItems]);
  const transforToData = useCallback(items => {
    const placeholderData = configuration.reduce((acc, cur) => {
      acc[cur.key] = undefined;
      return acc;
    }, {});
    return items.slice(0, -1).reduce((acc, cur) => {
      acc[cur.key] = cur.value;
      return acc;
    }, placeholderData);
  }, [configuration]);

  // 包装 onSearch
  const submit = items => {
    setSettledItems(items);
    onSearch(transforToData(items));
  };

  // configuration 变更时检查 settledItems
  useUpdateEffect(() => {
    if (settledItems.length > 1) {
      const newSettledItems = settledItems.reduce((acc, cur) => {
        const newItem = configuration.find(item => cur.key === item.key);
        return newItem ? [...acc, {
          ...cur,
          ...newItem
        }] : acc;
      }, []);
      setSettledItems([...newSettledItems, last(settledItems)]);
    }
  }, [configuration]);

  // value 变更时重置 settledItems
  useUpdateEffect(() => {
    if (!equals(value, transforToData(settledItems))) {
      let _value = clone(value);
      const newSettledItems = settledItems.slice(0, -1).reduce((acc, cur) => {
        const v = _value === null || _value === void 0 ? void 0 : _value[cur.key];
        if (!isNil(v)) {
          acc.push({
            ...cur,
            value: v
          });
          _value === null || _value === void 0 || delete _value[cur.key];
        }
        return acc;
      }, []);
      setSettledItems([...newSettledItems, ...formatSearchValue(_value !== null && _value !== void 0 ? _value : {}, configuration), last(settledItems)]);
    }
  }, [value]);
  const focus = useMemoizedFn(key => {
    const itemKey = key !== null && key !== void 0 ? key : last(settledItems).key;
    if (itemKey) {
      window.setTimeout(() => {
        setFocusItemKey({
          [itemKey]: Date.now()
        });
      }, 0);
    }
  });
  const clear = useMemoizedFn(() => {
    submit(settledItems.slice(-1));
  });

  // 提供自定义的 ref
  useImperativeHandle(ref, () => ({
    focus,
    clear,
    blur
  }));
  useEffect(() => {
    toggleClearIcon(settledItems.length > 1);
  }, [settledItems.length]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: settledItems.map((item, idx) => {
      const isLastItem = idx === settledItems.length - 1;
      const freeItems = configuration.reduce((acc, cur) => {
        var _cur$options;
        const notInSettledItems = !settledItemkeys.includes(cur.key) || cur.key === item.key;
        // 过滤掉mode为select但是option为空的item
        const ifSelectAndHasOption = cur.mode !== 'text' ? !!((_cur$options = cur.options) !== null && _cur$options !== void 0 && _cur$options.length) : true;
        if (notInSettledItems && ifSelectAndHasOption) {
          acc.push(cur);
        }
        return acc;
      }, []);
      let value = '';
      if (item.label) {
        value = "".concat(item.label, ": ");
        //  multiple-select
        if (item.mode === 'multiple-select') {
          var _item$options;
          const valueStr = ((_item$options = item.options) !== null && _item$options !== void 0 ? _item$options : []).reduce((acc, cur) => {
            if (item.value.includes(cur.value)) {
              acc = acc ? "".concat(acc, " | ").concat(cur.label) : cur.label;
            }
            return acc;
          }, '');
          value += valueStr;
        }
        // single-select
        else if (item.mode === 'single-select') {
          var _item$options$find$la, _item$options2;
          value += (_item$options$find$la = (_item$options2 = item.options) === null || _item$options2 === void 0 || (_item$options2 = _item$options2.find(opt => opt.value === item.value)) === null || _item$options2 === void 0 ? void 0 : _item$options2.label) !== null && _item$options$find$la !== void 0 ? _item$options$find$la : '';
        }
        // text
        else {
          value += item.value;
        }
      }
      return /*#__PURE__*/_jsx(Item, {
        value: value,
        prefixClass: baseClassName,
        freeItems: freeItems,
        isFocusWithin: isFocusWithin,
        focusId: focusItemKey[item.key],
        placeholder: placeholder,
        onChange: (action, newItem) => {
          let newSettledItems = [];
          switch (action) {
            case 'update':
              if (newItem) {
                newSettledItems = update(idx, newItem, settledItems);
                if (isLastItem) {
                  newSettledItems.push({
                    key: generateUuid(),
                    label: '',
                    value: '',
                    mode: 'text'
                  });
                }
                submit(newSettledItems);
                focus(last(newSettledItems).key);
              }
              break;
            case 'remove':
              newSettledItems = remove(idx, 1, settledItems);
              submit(newSettledItems);
              break;
            case 'backspace':
              if (isLastItem) {
                if (idx > 0) {
                  newSettledItems = remove(idx - 1, 1, settledItems);
                  submit(newSettledItems);
                }
              } else {
                newSettledItems = remove(idx, 1, settledItems);
                submit(newSettledItems);
                focus(last(newSettledItems).key);
              }
              break;
            default:
              break;
          }
        }
      }, item.key);
    })
  });
});
export default GroupSearch;